import { AttachmentField } from '$src/components/common/AttachmentField'
import { useLingui } from '@lingui/react/macro'
import { useAttachments } from '$src/lib/hooks/useAttachments'
import { maintenanceLogHooks, maintenanceTaskHooks } from '$src/lib/trpcHooks'
import {
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { AppModal } from '@markku/components'
import { categoryOptions } from '@markku/utils'
import { IconCoinEuro } from '@tabler/icons-react'
import { useEffect, useState } from 'react'

interface CostEntryModalProps {
  propertyId: number
  opened: boolean
  onClose: () => void
}

interface CostFormData {
  name: string
  completedDate: string
  actualCost: number | undefined
  category: string
  notes: string
}

export function CostEntryModal({
  propertyId,
  opened,
  onClose,
}: CostEntryModalProps) {
  const { t } = useLingui()
  const [isSaving, setIsSaving] = useState(false)
  const attachments = useAttachments('maintenance_log')

  const form = useForm<CostFormData>({
    initialValues: {
      name: '',
      completedDate: new Date().toISOString().split('T')[0],
      actualCost: undefined,
      category: '',
      notes: '',
    },
    validate: {
      name: (value) => (!value.trim() ? t`Cost name is required` : null),
      completedDate: (value) => (!value ? t`Date is required` : null),
    },
  })

  const { mutateAsync: createTask } = maintenanceTaskHooks.useCreate()
  const { mutateAsync: createLog } = maintenanceLogHooks.useCreate()

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      form.reset()
      attachments.setInitialAttachments([])
      form.clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  const handleSave = async () => {
    // Validate form
    if (form.validate().hasErrors) {
      notifications.show({
        title: t`Error`,
        message: t`Check the form data`,
        color: 'red',
      })
      return
    }

    setIsSaving(true)
    try {
      // Create minimal task (deadline = completed date, no estimated cost)
      const task = await createTask({
        data: {
          propertyId,
          name: form.values.name,
          deadline: form.values.completedDate,
          category: form.values.category || undefined,
          description: form.values.notes || undefined,
          estimatedCost: undefined,
        },
      })

      // Create completion log
      await createLog({
        data: {
          propertyId,
          maintenanceTaskId: task.id,
          taskNameSnapshot: form.values.name,
          descriptionSnapshot: form.values.notes || undefined,
          completedDate: form.values.completedDate,
          notes: form.values.notes || undefined,
          actualCost: form.values.actualCost?.toString(),
        },
        attachments: attachments.getChanges(),
      })

      notifications.show({
        title: t`Success`,
        message: t`Cost saved`,
        color: 'green',
      })

      onClose()
    } catch (error) {
      console.error('Error saving cost:', error)
      notifications.show({
        title: t`Error`,
        message: t`Failed to save cost`,
        color: 'red',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={t`Add cost`}
      confirmProps={{
        label: t`Save`,
        onClick: handleSave,
        loading: isSaving,
      }}
      cancelProps={{
        label: t`Cancel`,
      }}
      showFooter={true}
    >
      <Stack gap="md">
        <TextInput
          label={t`Cost name`}
          placeholder={t`E.g. Pipe renovation, HVAC maintenance`}
          value={form.values.name}
          onChange={(e) => form.setFieldValue('name', e.currentTarget.value)}
          error={form.errors.name}
          required
          disabled={isSaving}
        />

        <DateInput
          label={t`Date`}
          placeholder={t`Select date`}
          value={
            form.values.completedDate
              ? new Date(form.values.completedDate)
              : new Date()
          }
          onChange={(value) => {
            form.setFieldValue('completedDate', value || '')
          }}
          error={form.errors.completedDate}
          required
          disabled={isSaving}
        />

        <NumberInput
          label={t`Cost`}
          placeholder="0"
          leftSection={<IconCoinEuro size={16} />}
          rightSection={
            <Text size="sm" c="dimmed">
              €
            </Text>
          }
          value={form.values.actualCost || ''}
          onChange={(value) =>
            form.setFieldValue(
              'actualCost',
              typeof value === 'number' ? value : undefined,
            )
          }
          min={0}
          max={100000}
          decimalScale={2}
          disabled={isSaving}
          description={t`Leave empty if no costs incurred`}
        />

        <Select
          label={t`Target`}
          placeholder={t`Select target`}
          data={categoryOptions}
          value={form.values.category}
          onChange={(value) => form.setFieldValue('category', value || '')}
          clearable
          disabled={isSaving}
        />

        <Textarea
          label={t`Notes`}
          placeholder={t`Add more detailed description...`}
          value={form.values.notes}
          onChange={(e) => form.setFieldValue('notes', e.currentTarget.value)}
          minRows={3}
          disabled={isSaving}
        />

        <div>
          <Stack gap="xs">
            <TextInput
              label={t`Attachments`}
              disabled
              style={{ display: 'none' }}
            />
            <AttachmentField
              attachments={attachments.attachments}
              isUploading={attachments.isUploading}
              uploadFile={attachments.uploadFile}
              removeAttachment={attachments.removeAttachment}
              disabled={isSaving}
            />
          </Stack>
        </div>
      </Stack>
    </AppModal>
  )
}
